/**
 * DefaultFeeSection Component
 * 협력사 기본 수수료 설정 섹션 (신규 라이더 자동 적용용)
 * 아코디언 형태로 접혔다 펼 수 있음
 */
import React from 'react';
import { useDefaultFeeSection } from '../hooks/useDefaultFeeSection';
import { COMMISSION_TYPE_OPTIONS } from '../utils/supplierFeeHelpers';
import { formatCurrency } from '@/shared/utils/formatter';
import FeeApplyDateModal from '../../shared/components/FeeApplyDateModal';
import '@/shared/styles/common-table.css';

interface DefaultFeeSectionProps {
    selectedSupplierId?: string;
    isReadOnly: boolean;
}

const DefaultFeeSection: React.FC<DefaultFeeSectionProps> = ({ selectedSupplierId, isReadOnly }) => {
    const {
        loading,
        defaultFees,
        setDefaultFees,
        showAddForm,
        setShowAddForm,
        isEditMode,
        isExpanded,
        showFeeApplyModal,
        feeApplyDateInfo,
        handleCommissionTypeSelectChange,
        handleAmountInputChange,
        handleSaveAndExitEditMode,
        handleEnterEditMode,
        handleCancelEdit,
        toggleExpanded,
        closeFeeApplyModal,
    } = useDefaultFeeSection({ selectedSupplierId, isReadOnly });

    if (loading) {
        return (
            <div style={{ padding: '20px', backgroundColor: 'var(--color-white)', borderRadius: 'var(--border-radius-lg)', marginBottom: '24px', border: '1px solid var(--color-gray-200)' }}>
                <p style={{ textAlign: 'center', color: 'var(--color-gray-600)' }}>기본 수수료 정보를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* 아코디언 헤더 */}
            <div
                onClick={toggleExpanded}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(to bottom, #f0fdf4, #dcfce7)',
                    border: '1px solid #d8ffe5',
                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(5, 150, 105, 0.08)',
                    userSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#28a745', fontSize: '0.875rem' }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#28a745' }}>기본 수수료 설정</span>
                    {defaultFees.length > 0 && (
                        <span style={{ fontSize: '0.875rem', color: '#495057', fontWeight: '500' }}>
                            (현재 적용: {COMMISSION_TYPE_OPTIONS.find((opt: { value: string; label: string }) => opt.value === defaultFees[0].commissionType)?.label || '건당 정액'} /
                            <span style={{ color: '#04B374', fontWeight: '600' }}>
                                {defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED'
                                    ? ` ${defaultFees[0].feeAmt || 0}원`
                                    : ` ${defaultFees[0].feeRate || 0}%`}
                            </span>)
                        </span>
                    )}
                </div>
                <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{isExpanded ? '접기' : '펼치기'}</span>
            </div>

            {/* 아코디언 내용 */}
            {isExpanded && (
                <div style={{ background: '#ffffff', border: '1px solid #d8ffe5', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px', boxShadow: '0 1px 3px rgba(5, 150, 105, 0.08)' }}>
                    {defaultFees.length > 0 && !isEditMode && !defaultFees[0].isNew ? (
                        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '16px' }}>
                            <div style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 12px', borderRadius: '16px', fontSize: '0.875rem', fontWeight: '500', marginRight: '16px' }}>현재 적용 중</div>
                            <span style={{ fontSize: '0.875rem', color: '#495057', fontWeight: '500' }}>
                                수수료구분 : <span style={{ fontWeight: '600' }}>{COMMISSION_TYPE_OPTIONS.find((opt: { value: string; label: string }) => opt.value === defaultFees[0].commissionType)?.label || '건당 정액'}</span>
                                &nbsp;&nbsp; 금액 : <span style={{ color: '#04B374', fontWeight: '600' }}>
                                    {defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED'
                                        ? `${defaultFees[0].feeAmt || 0} 원`
                                        : `${defaultFees[0].feeRate || 0} %`}
                                </span>
                                &nbsp;&nbsp; 적용일 : <span style={{ fontWeight: '600' }}>{defaultFees[0].startDay} ~</span>
                            </span>
                            <div style={{ marginLeft: 'auto' }}>
                                {!isReadOnly && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleEnterEditMode(); }}
                                        style={{ backgroundColor: '#ffffff', color: '#6c757d', border: '1px solid #dee2e6', padding: '6px 12px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer' }}
                                    >
                                        수수료 변경
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 12px', borderRadius: '16px', fontSize: '0.875rem', fontWeight: '500', marginRight: '12px' }}>
                                    {defaultFees.length > 0 ? '수정 모드' : '신규 설정'}
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#6c757d', fontWeight: '500' }}>
                                    {defaultFees.length > 0 ? '기본 수수료를 수정해주세요' : '기본 수수료를 설정해주세요'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{ minWidth: '80px', fontSize: '0.875rem', fontWeight: '500', color: '#495057' }}>수수료구분 :</span>
                                <select
                                    value={defaultFees.length > 0 ? defaultFees[0].commissionType : 'PER_CASE_FIXED'}
                                    onChange={(e) => handleCommissionTypeSelectChange(e.target.value)}
                                    disabled={isReadOnly}
                                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', minWidth: '120px' }}
                                >
                                    {COMMISSION_TYPE_OPTIONS.map((option: { value: string; label: string }) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#495057' }}>금액 :</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                        type="text"
                                        value={defaultFees.length > 0
                                            ? ((defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED')
                                                ? (defaultFees[0].feeAmt ? formatCurrency(defaultFees[0].feeAmt.toString()) : '')
                                                : (defaultFees[0].feeRate ? defaultFees[0].feeRate.toString() : ''))
                                            : ''}
                                        onChange={(e) => handleAmountInputChange(e.target.value)}
                                        placeholder="금액"
                                        disabled={isReadOnly}
                                        style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', width: '80px', textAlign: 'right' }}
                                    />
                                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                        {defaultFees.length > 0 && (defaultFees[0].commissionType === 'PER_CASE_RATE' || defaultFees[0].commissionType === 'PER_DAY_RATE') ? '%' : '원'}
                                    </span>
                                </div>
                                {!isReadOnly && (
                                    <button
                                        onClick={handleSaveAndExitEditMode}
                                        style={{ backgroundColor: '#05CD82', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', marginLeft: '8px' }}
                                    >
                                        저장
                                    </button>
                                )}
                                {isEditMode && (
                                    <button
                                        onClick={handleCancelEdit}
                                        style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', marginLeft: '8px' }}
                                    >
                                        취소
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <div style={{ padding: '12px 16px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', fontSize: '0.875rem', color: '#1976d2' }}>
                        <i className="fas fa-lightbulb" style={{ marginRight: '8px' }}></i>
                        <strong>TIP:</strong> 여기서 설정한 기본 수수료는 신규 라이더가 회원가입할 때 자동으로 적용됩니다.
                    </div>
                </div>
            )}

            {/* 수수료 적용 예정일 모달 */}
            {feeApplyDateInfo && (
                <FeeApplyDateModal
                    isOpen={showFeeApplyModal}
                    applyStartDate={feeApplyDateInfo.applyStartDate}
                    isImmediateApply={feeApplyDateInfo.isImmediateApply}
                    onClose={closeFeeApplyModal}
                />
            )}
        </div>
    );
};

export default DefaultFeeSection;
